/**
 * MTE UpdatePassword form — Phase 4 auth UI structure (M-C2.0+ pre-infra).
 *
 * Form new password (após callback from email reset link). ZERO fetch / ZERO supabase.
 * Após setup infra: wire ao POST /api/auth/update-password (D-AUTH-02 v1.2).
 *
 * Note: o token de reset vem via hash da URL (PKCE flow Supabase). Aqui só o form.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Layout, Header } from '../../components/mte/shell';
import '../../styles/auth.css';

export const UpdatePasswordSchema = z.object({
  password: z.string().min(8, 'Mot de passe : au moins 8 caractères').max(72, 'Mot de passe : 72 caractères maximum'),
  confirm: z.string(),
}).refine((d) => d.password === d.confirm, {
  message: 'Les mots de passe ne correspondent pas',
  path: ['confirm'],
});

export type UpdatePasswordForm = z.infer<typeof UpdatePasswordSchema>;

interface UpdatePasswordProps {
  onSubmit?: (data: UpdatePasswordForm) => Promise<void> | void;
}

function UpdatePassword(props: UpdatePasswordProps = {}): JSX.Element {
  const navigate = useNavigate();
  const [submitState, setSubmitState] = useState<{ status: 'idle' | 'submitting' | 'error'; message: string }>({ status: 'idle', message: '' });

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<UpdatePasswordForm>({
    resolver: zodResolver(UpdatePasswordSchema),
    defaultValues: { password: '', confirm: '' },
  });

  const onValid = async (data: UpdatePasswordForm): Promise<void> => {
    setSubmitState({ status: 'submitting', message: '' });
    try {
      if (props.onSubmit) await props.onSubmit(data);
      navigate('/login/', { replace: true });
    } catch (e) {
      setSubmitState({ status: 'error', message: e instanceof Error ? e.message : 'Erreur inconnue' });
    }
  };

  return (
    <Layout context="adulte" width="narrow" header={<Header context="adulte" />}>
      <main className="auth-page">
        <div className="auth-card">
          <h1 className="auth-h1">Choisis un nouveau mot de passe</h1>
          {submitState.status === 'error' && (
            <div className="auth-error-region" role="alert" aria-live="assertive" data-testid="update-error-region">
              {submitState.message}
            </div>
          )}
          <form className="auth-form" onSubmit={handleSubmit(onValid)} noValidate data-testid="update-form">
            <div className="auth-field">
              <label htmlFor="password" className="auth-label">Nouveau mot de passe</label>
              <input id="password" type="password" autoComplete="new-password" className="auth-input"
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? 'upd-password-error' : undefined}
                {...register('password')} />
              {errors.password && <span id="upd-password-error" className="auth-error" role="alert">{errors.password.message}</span>}
            </div>
            <div className="auth-field">
              <label htmlFor="confirm" className="auth-label">Confirme le mot de passe</label>
              <input id="confirm" type="password" autoComplete="new-password" className="auth-input"
                aria-invalid={Boolean(errors.confirm)}
                aria-describedby={errors.confirm ? 'upd-confirm-error' : undefined}
                {...register('confirm')} />
              {errors.confirm && <span id="upd-confirm-error" className="auth-error" role="alert">{errors.confirm.message}</span>}
            </div>
            <button type="submit" className="auth-button-primary" disabled={isSubmitting} data-testid="update-submit">
              {isSubmitting ? 'Mise à jour…' : 'Mettre à jour'}
            </button>
            <p className="auth-help-text" style={{ textAlign: 'center' }}>
              <Link to="/login/" className="auth-link">Annuler</Link>
            </p>
          </form>
        </div>
      </main>
    </Layout>
  );
}

export default UpdatePassword;
